import "./proxyHealth.scss";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import moment from "moment";
import { axiosInstance } from "../../utils/axiosInstance";
import PageContainer from "../layouts/PageContainer";
import {
  Card,
  CollapsibleFilters,
  EmptyState,
  ErrorBanner,
  LoadingPanel,
  PageHeader,
  Pill,
  RefreshButton,
  Tone,
} from "../ui";
import {
  PROXY_COMMAND_CLIENTS,
  PROXY_COMMAND_NAMES,
  PROXY_COMMAND_STATUSES,
  ProxyCommandHistoryPayload,
  ProxyCommandRow,
} from "./proxyHealthTypes";

const PAGE_SIZE = 50;

type FilterState = {
  q: string;
  status: string;
  command: string;
  client: string;
  macId: string;
  locationApiId: string;
  from: string;
  to: string;
  page: number;
};

function readFilters(params: URLSearchParams): FilterState {
  return {
    q: params.get("q") || "",
    status: params.get("status") || "",
    command: params.get("command") || "",
    client: params.get("client") || "",
    macId: params.get("macId") || "",
    locationApiId: params.get("locationApiId") || "",
    from: params.get("from") || "",
    to: params.get("to") || "",
    page: Math.max(1, Number(params.get("page")) || 1),
  };
}

function writeFilters(f: FilterState): URLSearchParams {
  const p = new URLSearchParams();
  if (f.q.trim()) p.set("q", f.q.trim());
  if (f.status) p.set("status", f.status);
  if (f.command) p.set("command", f.command);
  if (f.client) p.set("client", f.client);
  if (f.macId.trim()) p.set("macId", f.macId.trim());
  if (f.locationApiId.trim()) p.set("locationApiId", f.locationApiId.trim());
  if (f.from) p.set("from", f.from);
  if (f.to) p.set("to", f.to);
  if (f.page > 1) p.set("page", String(f.page));
  return p;
}

/** datetime-local value ↔ ISO for API */
function localInputToIso(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  return Number.isFinite(d.getTime()) ? d.toISOString() : undefined;
}

function isoToLocalInput(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function formatWhen(iso: string): string {
  const m = moment(iso);
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm:ss") : iso;
}

function statusTone(status: string): Tone {
  if (status === "COMPLETED") return "good";
  if (status === "FAILED") return "bad";
  if (status === "PENDING" || status === "QUEUED") return "warn";
  return "neutral";
}

function formatParams(params: unknown): string {
  if (params == null) return "—";
  try {
    return JSON.stringify(params, null, 2);
  } catch {
    return String(params);
  }
}

export default function ProxyCommandHistory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readFilters(searchParams), [searchParams]);

  const [data, setData] = useState<ProxyCommandHistoryPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  /** Draft inputs — applied to URL on Change / Enter (except selects apply immediately). */
  const [draftQ, setDraftQ] = useState(filters.q);
  const [draftMac, setDraftMac] = useState(filters.macId);
  const [draftLoc, setDraftLoc] = useState(filters.locationApiId);
  const [draftFrom, setDraftFrom] = useState(
    filters.from ? isoToLocalInput(filters.from) : ""
  );
  const [draftTo, setDraftTo] = useState(
    filters.to ? isoToLocalInput(filters.to) : ""
  );

  useEffect(() => {
    setDraftQ(filters.q);
    setDraftMac(filters.macId);
    setDraftLoc(filters.locationApiId);
    setDraftFrom(filters.from ? isoToLocalInput(filters.from) : "");
    setDraftTo(filters.to ? isoToLocalInput(filters.to) : "");
  }, [filters]);

  const patchFilters = useCallback(
    (patch: Partial<FilterState>, resetPage = true) => {
      const next: FilterState = {
        ...filters,
        ...patch,
        page: resetPage ? 1 : patch.page ?? filters.page,
      };
      setSearchParams(writeFilters(next), { replace: true });
    },
    [filters, setSearchParams]
  );

  const applyTextFilters = useCallback(() => {
    patchFilters({
      q: draftQ,
      macId: draftMac,
      locationApiId: draftLoc,
      from: localInputToIso(draftFrom) || "",
      to: localInputToIso(draftTo) || "",
    });
  }, [draftQ, draftMac, draftLoc, draftFrom, draftTo, patchFilters]);

  const load = useCallback(
    async (opts?: { soft?: boolean }) => {
      if (opts?.soft) setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const params: Record<string, string | number> = {
          page: filters.page,
          pageSize: PAGE_SIZE,
        };
        if (filters.q.trim()) params.q = filters.q.trim();
        if (filters.status) params.status = filters.status;
        if (filters.command) params.command = filters.command;
        if (filters.client) params.client = filters.client;
        if (filters.macId.trim()) params.macId = filters.macId.trim();
        if (filters.locationApiId.trim())
          params.locationApiId = filters.locationApiId.trim();
        if (filters.from) params.from = filters.from;
        if (filters.to) params.to = filters.to;

        const res = await axiosInstance.get<ProxyCommandHistoryPayload>(
          "/ops/proxy-health/commands",
          { params }
        );
        setData(res.data);
        setExpandedId(null);
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ||
          (err as Error)?.message ||
          "Failed to load command history";
        setError(msg);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const pagination = data?.pagination;
  const rows = data?.data ?? [];
  const activeFilters = [
    filters.status,
    filters.command,
    filters.client,
    filters.macId,
    filters.locationApiId,
    filters.from,
    filters.to,
  ].filter(Boolean).length;

  return (
    <PageContainer>
      <PageHeader
        back={{ to: "/proxy-health", label: "Alliance" }}
        title="Command history"
        subtitle={`Alliance command ledger via sqi-proxy.${
          pagination
            ? ` ${pagination.totalRows} row${pagination.totalRows === 1 ? "" : "s"}`
            : ""
        }${refreshing ? " · refreshing…" : ""}`}
        actions={
          <RefreshButton onClick={() => load({ soft: true })} loading={loading || refreshing} />
        }
      />

      {error ? <ErrorBanner onRetry={() => load()}>{error}</ErrorBanner> : null}

      <Card flush>
        <form
          className="sq-toolbar"
          onSubmit={(e) => {
            e.preventDefault();
            applyTextFilters();
          }}
        >
          <label className="sq-field is-grow">
            <span>Search</span>
            <input
              type="search"
              className="sq-input"
              value={draftQ}
              onChange={(e) => setDraftQ(e.target.value)}
              placeholder="Mac, error, requested by"
            />
          </label>
          <CollapsibleFilters activeCount={activeFilters}>
            <label className="sq-field is-half">
              <span>Status</span>
              <select
                className="sq-input"
                value={filters.status}
                onChange={(e) => patchFilters({ status: e.target.value })}
              >
                <option value="">All</option>
                {PROXY_COMMAND_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className="sq-field is-half">
              <span>Command</span>
              <select
                className="sq-input"
                value={filters.command}
                onChange={(e) => patchFilters({ command: e.target.value })}
              >
                <option value="">All</option>
                {PROXY_COMMAND_NAMES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="sq-field is-half">
              <span>Client</span>
              <select
                className="sq-input"
                value={filters.client}
                onChange={(e) => patchFilters({ client: e.target.value })}
              >
                <option value="">All</option>
                {PROXY_COMMAND_CLIENTS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="sq-field is-half">
              <span>Mac</span>
              <input
                type="text"
                className="sq-input"
                value={draftMac}
                onChange={(e) => setDraftMac(e.target.value)}
                placeholder="Prefix"
              />
            </label>
            <label className="sq-field">
              <span>Location</span>
              <input
                type="text"
                className="sq-input"
                value={draftLoc}
                onChange={(e) => setDraftLoc(e.target.value)}
                placeholder="Location API id"
              />
            </label>
            <label className="sq-field">
              <span>From</span>
              <input
                type="datetime-local"
                className="sq-input"
                value={draftFrom}
                onChange={(e) => setDraftFrom(e.target.value)}
              />
            </label>
            <label className="sq-field">
              <span>To</span>
              <input
                type="datetime-local"
                className="sq-input"
                value={draftTo}
                onChange={(e) => setDraftTo(e.target.value)}
              />
            </label>
          </CollapsibleFilters>
          <button type="submit" className="btn btn-sm btn-primary">
            Apply
          </button>
        </form>

        {loading && !data ? <LoadingPanel label="Loading command history" rows={8} /> : null}

        {!loading || data ? (
          <>
            {rows.length === 0 && !error ? (
              <EmptyState title="No commands match these filters" />
            ) : (
              <div className="sq-table-wrap">
                <table className="sq-table is-stacked">
                  <thead>
                    <tr>
                      <th scope="col" aria-label="Expand" />
                      <th scope="col">Time</th>
                      <th scope="col">Status</th>
                      <th scope="col">Command</th>
                      <th scope="col">Machine</th>
                      <th scope="col">Location</th>
                      <th scope="col">Client</th>
                      <th scope="col">Requested by</th>
                      <th scope="col">Attempts</th>
                      <th scope="col">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row: ProxyCommandRow) => {
                      const open = expandedId === row.id;
                      const toggle = () => setExpandedId(open ? null : row.id);
                      return (
                        <Fragment key={row.id}>
                          <tr>
                            <td className="cell-actions">
                              <button
                                type="button"
                                className="sq-row-toggle"
                                aria-expanded={open}
                                aria-label={open ? "Collapse details" : "Expand details"}
                                onClick={toggle}
                              >
                                {open ? (
                                  <IconChevronDown size={18} />
                                ) : (
                                  <IconChevronRight size={18} />
                                )}
                              </button>
                            </td>
                            <td data-label="Time" className="nowrap" title={row.created_at}>
                              {formatWhen(row.created_at)}
                            </td>
                            <td data-label="Status">
                              <Pill tone={statusTone(row.status)}>{row.status}</Pill>
                            </td>
                            <td className="cell-title">
                              <span className="sq-mono">{row.command}</span>
                            </td>
                            <td data-label="Machine">
                              <span className="sq-mono">{row.mac_id}</span>
                            </td>
                            <td data-label="Location">
                              <span className="sq-mono">{row.location_api_id}</span>
                            </td>
                            <td data-label="Client">{row.client}</td>
                            <td data-label="Requested by">{row.requested_by || "—"}</td>
                            <td data-label="Attempts">{row.attempts}</td>
                            <td
                              data-label="Error"
                              className={`truncate cell-wide${row.last_error ? "" : " is-empty"}`}
                              title={row.last_error || undefined}
                            >
                              {row.last_error || "—"}
                            </td>
                          </tr>
                          {open ? (
                            <tr className="is-detail">
                              <td colSpan={10}>
                                <dl className="proxy-detail">
                                  <dt>Ledger id</dt>
                                  <dd>{row.id}</dd>
                                  <dt>Alliance id</dt>
                                  <dd>
                                    {row.alliance_command_id ? (
                                      <span className="sq-mono">{row.alliance_command_id}</span>
                                    ) : (
                                      "—"
                                    )}
                                  </dd>
                                  <dt>Idempotency</dt>
                                  <dd>{row.idempotency_key || "—"}</dd>
                                  <dt>Updated</dt>
                                  <dd>{formatWhen(row.updated_at)}</dd>
                                  <dt>Last error</dt>
                                  <dd>{row.last_error || "—"}</dd>
                                  <dt>Params</dt>
                                  <dd>
                                    <pre>{formatParams(row.params_json)}</pre>
                                  </dd>
                                </dl>
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {pagination && pagination.totalPages > 0 ? (
              <div className="sq-pagination">
                <span>
                  Page {pagination.page} of {pagination.totalPages} · {pagination.totalRows} total
                </span>
                <div className="sq-page-btns">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={pagination.page <= 1 || loading || refreshing}
                    onClick={() => patchFilters({ page: pagination.page - 1 }, false)}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={pagination.page >= pagination.totalPages || loading || refreshing}
                    onClick={() => patchFilters({ page: pagination.page + 1 }, false)}
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </Card>
    </PageContainer>
  );
}
